import { Loading } from '@carbon/react';
/**
 * Composant pour charger le bootstrap de l'application après l'authentification
 */
import { useEffect } from 'react';
import { useBootstrap, useKeycloak } from '@sphere/auth/data-access';
import { ErrorView } from '@sphere/shared/ui/design-system';
import { useAccessStore } from '@sphere/auth/data-access';
import {
  configureAuthGetter,
  configureUnauthorizedHandler,
} from '@sphere/gateway/shared';
import { useUpdateLastSeen } from '@sphere/user/data-access';

import { useWebPushDeviceRegistration } from './useWebPushDeviceRegistration';

interface BootstrapLoaderProps {
  children: React.ReactNode;
}

/**
 * Composant qui charge le bootstrap et affiche l'application une fois prêt
 * Gère les états de chargement et d'erreur
 */
export function BootstrapLoader({ children }: BootstrapLoaderProps) {
  const {
    authenticated,
    initialized: keycloakInitialized,
    token,
    keycloakInstance,
  } = useKeycloak();
  const bootstrapLoaded = useAccessStore((state) => state.bootstrapLoaded);

  // Sync token provider with Gateway shared HTTP client
  useEffect(() => {
    if (keycloakInitialized && keycloakInstance) {
      configureAuthGetter(() => keycloakInstance.token || null);

      // En cas de 401 (Token expiré / invalide), on force le login via Keycloak
      configureUnauthorizedHandler(() => {
        keycloakInstance.login();
      });
    }
  }, [keycloakInitialized, keycloakInstance]);

  // Mettre à jour le lastSeenAt lors de la connexion
  const updateLastSeen = useUpdateLastSeen();
  useEffect(() => {
    if (authenticated && bootstrapLoaded) {
      updateLastSeen.mutate();
    }
  }, [authenticated, bootstrapLoaded]);

  useWebPushDeviceRegistration(Boolean(authenticated && bootstrapLoaded));

  // Charger le bootstrap uniquement si authentifié
  const {
    isLoading: bootstrapLoading,
    error: bootstrapError,
    refetch,
  } = useBootstrap({
    enabled: authenticated && keycloakInitialized,
  });

  // Attendre l'init de Keycloak
  if (!keycloakInitialized) {
    return <Loading description="Initialisation..." withOverlay />;
  }

  // Si non authentifié, l'utilisateur sera redirigé par Keycloak
  if (!authenticated) {
    return <Loading description="Authentification..." withOverlay />;
  }

  // Attendre le chargement du bootstrap
  if (bootstrapLoading && !bootstrapLoaded) {
    return <Loading description="Chargement de votre profil..." withOverlay />;
  }

  // Afficher l'erreur si le chargement du bootstrap a échoué
  // ✅ Ignorer les erreurs BOOTSTRAP_CANCELED (utilisateur non authentifié)
  if (bootstrapError && !bootstrapLoaded) {
    const error = bootstrapError as Error;
    // Si c'est une erreur d'annulation, on affiche juste le loader
    if (error?.message === 'BOOTSTRAP_CANCELED') {
      return <Loading description="Authentification..." withOverlay />;
    }

    return (
      <ErrorView
        title="Erreur de chargement"
        error={error}
        onRetry={() => refetch()}
        withOverlay
      />
    );
  }

  // Rendu des enfants sans bandeau de mode dégradé
  // Le backend gère les permissions, on affiche simplement les données disponibles
  console.log(
    `✅ [FRONTEND] Bootstrap loaded, rendering app at ${new Date().toISOString()}`
  );
  return <>{children}</>;
}
